import styled from '@emotion/styled'
import {
  AiOutlineCheckCircle,
  AiOutlineExclamationCircle,
  AiOutlineInfoCircle,
} from 'react-icons/ai'
import Text from '../../components/common/Text'
import BaseButton from '../../components/common/Button'
import { Modal } from '../../components/common/Modal'
import { BACKGROUND, DARK_GRAY, ORANGE, WHITE } from '../../constant/colors'
import { mobile, useScreen } from '../../utils/responsive-helper'
import { useHistory } from 'react-router'
import * as paths from '../../constant/path'
import { useVisibility } from '../../utils/custom-hook'
import CouponInformation from '../../components/CouponInformation'
import { useCollectCoupon, useGetCouponList } from '../../services/coupon/coupon-query'
import dayjs from 'dayjs'
import { CouponDetail } from '../../services/coupon/coupon-typed'
import { useState } from 'react'
import toast, { Toaster } from 'react-hot-toast'

const ContainerStyle = styled.div`
  display: flex;
  flex-wrap: no-wrap;
  overflow-x: auto;
  overflow-y: hidden;
  margin-bottom: 16px;
`

const ScrollStyle = styled.div`
  display: flex;
  overflow-x: scroll;
`

const SliderStyle = styled.div`
  display: flex;
  flex: 0 0 auto;
  width: 318px;
  height: 120px;
  margin-right: 10px;
  background: linear-gradient(270deg, #ffb292 0%, ${ORANGE} 100%);
  border-radius: 4px;

  ${mobile} {
    width: 212px;
    height: 86px;
  }
`

const DotLeftStyle = styled.div`
  position: relative;
  top: 52px;
  left: -6px;
  width: 16px;
  height: 16px;
  background: ${BACKGROUND};
  border-radius: 50%;
  display: inline-flex;

  ${mobile} {
    top: 35px;
    left: -6px;
  }
`

const DotRightStyle = styled.div`
  position: relative;
  top: 52px;
  left: 296px;
  width: 16px;
  height: 16px;
  background: ${BACKGROUND};
  border-radius: 50%;
  display: inline-flex;

  ${mobile} {
    top: 35px;
    left: 190px;
  }
`

const ContentStyle = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  padding-right: 16px;
  flex-direction: column;
  justify-content: center;
`

const HeaderStyle = styled.div`
  display: flex;
  justify-content: space-between;
`

const HeaderTextStyle = styled(Text)`
  font-size: 20px;
  margin-bottom: 4px;
  padding-right: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;

  ${mobile} {
    font-size: 16px;
  }
`

const BodyStyle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
`

const DetailStyle = styled.div``

const DetailTextStyle = styled(Text)`
  font-size: 16px;

  ${mobile} {
    font-size: 12px;
  }
`

const ButtonStyle = styled(BaseButton)`
  width: 36px;
  height: 24px;
  line-height: 24px;
  border: none;
  border-radius: 36px;
  margin-left: 4px;
  padding: 0px;
`

const SuccessToastStyle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  color: ${WHITE};
  background: #53b64b;
  border-radius: 4px;
  padding: 8px;
`

const FailToastStyle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  color: ${WHITE};
  background: #c11d1a;
  border-radius: 4px;
  padding: 8px;
`

const ContentToastStyle = styled(Text)`
  color: ${WHITE};
  padding: 0 24px 0 8px;
`

const UseCouponToastStyle = styled(Text)`
  color: ${WHITE};
  padding: 0;
  padding-right: 8px;
`

type CouponCardSliderProps = {
  path?: string
}

const CouponCardSlider = ({ path }: CouponCardSliderProps) => {
  const { isMobile } = useScreen()
  const [modalVisiblelity, modal] = useVisibility()
  const history = useHistory()
  const { data: couponList } = useGetCouponList()
  const [couponDetail, setCouponDetail] = useState<CouponDetail>()
  const { mutate: collectCoupon } = useCollectCoupon()

  const handleMoreInformation = (coupon: CouponDetail) => {
    setCouponDetail(coupon)
    isMobile
      ? history.push(
          paths.couponDetail({
            routeParam: {
              couponId: coupon.id,
            },
            queryParam: {
              page: path || paths.promotion(),
            },
          }),
        )
      : modal.show()
  }

  const handleGetCoupon = (couponId: number) => {
    collectCoupon(couponId, {
      onSuccess: () => {
        showToast(true)
      },
      onError: (error) => {
        showToast(false, error.message)
      },
    })
  }

  const showToast = (success: boolean, message?: string) => {
    if (success) {
      toast.custom(() => (
        <SuccessToastStyle>
          <AiOutlineCheckCircle />
          <ContentToastStyle>คุณได้รับคูปองแล้ว</ContentToastStyle>
          <UseCouponToastStyle size={14}>ใช้คูปองเลย</UseCouponToastStyle>
        </SuccessToastStyle>
      ))
    } else {
      toast.custom(() => (
        <FailToastStyle>
          <AiOutlineExclamationCircle />
          <ContentToastStyle>{message || 'something went wrong'}</ContentToastStyle>
        </FailToastStyle>
      ))
    }
  }

  return (
    <ContainerStyle>
      <ScrollStyle>
        {couponList?.map((coupon, index) => (
          <SliderStyle key={index}>
            <DotLeftStyle />
            <DotRightStyle />
            <ContentStyle>
              <HeaderStyle>
                <HeaderTextStyle color={WHITE} type="Bold">
                  {coupon.title}
                </HeaderTextStyle>
                <Text
                  color={WHITE}
                  style={{ paddingTop: 4 }}
                  onClick={() => handleMoreInformation(coupon)}
                >
                  <AiOutlineInfoCircle />
                </Text>
              </HeaderStyle>
              <BodyStyle>
                <DetailStyle>
                  <DetailTextStyle
                    color={WHITE}
                  >{`Min. spend ฿${coupon.minSpend}`}</DetailTextStyle>
                  <DetailTextStyle color={WHITE}>
                    {`Valid until ${dayjs(coupon.endDate).format('DD/MM/YYYY')}`}
                  </DetailTextStyle>
                </DetailStyle>
                <ButtonStyle
                  background={WHITE}
                  fontSize={12}
                  fontColor={DARK_GRAY}
                  onClick={() => handleGetCoupon(coupon.id)}
                >
                  GET
                </ButtonStyle>
              </BodyStyle>
            </ContentStyle>
          </SliderStyle>
        ))}
      </ScrollStyle>
      <Toaster position={isMobile ? 'bottom-center' : 'top-center'} />
      <Modal
        visible={modalVisiblelity}
        headerComponent={
          <Text type="SemiBold" size={24}>
            เงื่อนไขการใช้คูปอง
          </Text>
        }
        closeIconSize={24}
        onOk={() => {
          modal.hide()
        }}
        onCancel={modal.hide}
        hideFooter
        style={{ width: 480, height: '620px' }}
      >
        <CouponInformation couponDetail={couponDetail} />
      </Modal>
    </ContainerStyle>
  )
}

export default CouponCardSlider
